import { z } from 'zod';
import { createRpcContract, createRpcCaller } from '@/lib/rpc';

// ============================================
// TYPES
// ============================================

export interface AdminStats {
  totalUsers: number;
  freeUsers: number;
  powerRackUsers: number;
  trialUsers: number;
  adminUsers: number;
  suspendedUsers: number;
  totalPrograms: number;
  pendingPrograms: number;
  approvedPrograms: number;
  rejectedPrograms: number;
  flaggedPrograms: number;
  totalReviews: number;
  signupsToday: number;
  signupsThisWeek: number;
  signupsThisMonth: number;
}

export interface SignupTrend {
  date: string;
  count: number;
}

export interface TopProgram {
  id: string;
  name: string;
  authorName: string;
  downloads: number;
  rating: number;
  ratingCount: number;
  submittedAt: Date;
}

// ============================================
// RPC CONTRACTS
// ============================================

export const getAdminStatsContract = createRpcContract(
  'admin_get_stats',
  z.object({}),
  z.object({
    total_users: z.number(),
    free_users: z.number(),
    power_rack_users: z.number(),
    trial_users: z.number(),
    admin_users: z.number(),
    suspended_users: z.number(),
    total_programs: z.number(),
    pending_programs: z.number(),
    approved_programs: z.number(),
    rejected_programs: z.number(),
    flagged_programs: z.number(),
    total_reviews: z.number(),
    signups_today: z.number(),
    signups_this_week: z.number(),
    signups_this_month: z.number(),
  })
);

export const getSignupTrendsContract = createRpcContract(
  'admin_get_signup_trends',
  z.object({
    p_days: z.number().default(30),
  }),
  z.array(
    z.object({
      date: z.string(),
      count: z.number(),
    })
  )
);

export const getTopProgramsContract = createRpcContract(
  'admin_get_top_programs',
  z.object({
    p_limit: z.number().default(10),
    p_sort_by: z.enum(['downloads', 'rating', 'recent']).default('downloads'),
  }),
  z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      author_name: z.string(),
      downloads: z.number(),
      rating: z.number(),
      rating_count: z.number(),
      submitted_at: z.string(),
    })
  )
);

// ============================================
// RPC CALLERS
// ============================================

export const analyticsRpc = {
  getStats: createRpcCaller(getAdminStatsContract),
  getSignupTrends: createRpcCaller(getSignupTrendsContract),
  getTopPrograms: createRpcCaller(getTopProgramsContract),
};

// ============================================
// TRANSFORM HELPERS
// ============================================

export function transformStats(
  raw: z.infer<typeof getAdminStatsContract.outputSchema>
): AdminStats {
  return {
    totalUsers: raw.total_users,
    freeUsers: raw.free_users,
    powerRackUsers: raw.power_rack_users,
    trialUsers: raw.trial_users,
    adminUsers: raw.admin_users,
    suspendedUsers: raw.suspended_users,
    totalPrograms: raw.total_programs,
    pendingPrograms: raw.pending_programs,
    approvedPrograms: raw.approved_programs,
    rejectedPrograms: raw.rejected_programs,
    flaggedPrograms: raw.flagged_programs,
    totalReviews: raw.total_reviews,
    signupsToday: raw.signups_today,
    signupsThisWeek: raw.signups_this_week,
    signupsThisMonth: raw.signups_this_month,
  };
}

export function transformTopProgram(
  raw: z.infer<typeof getTopProgramsContract.outputSchema>[number]
): TopProgram {
  return {
    id: raw.id,
    name: raw.name,
    authorName: raw.author_name,
    downloads: raw.downloads,
    rating: raw.rating,
    ratingCount: raw.rating_count,
    submittedAt: new Date(raw.submitted_at),
  };
}
